import React, { useState } from 'react';
import { DateRangeFilter } from '../../components/common/DateRangeFilter';
import { useToast } from '../../components/common/Toast';
import { GeneralReportHeader } from './components/GeneralReportHeader';
import { ReportModeSelector } from './components/ReportModeSelector';
import { BusinessReportSection } from './components/BusinessReportSection';
import { FinishedGoodsReportSection } from './components/FinishedGoodsReportSection';
import { GreenBeansReportSection } from './components/GreenBeansReportSection';
import { InventoryTrackingDialog } from './components/InventoryTrackingDialog';
import { RoastedBeansReportSection } from './components/RoastedBeansReportSection';
import { RoastingReportSection } from './components/RoastingReportSection';
import { CustomerReportSection } from './components/CustomerReportSection';
import { ReceivablesSection } from './components/ReceivablesSection';
import { useReportPageState } from './hooks/useReportPageState';
import { useLanguage } from '../../features/language/useLanguage';
import { exportReportExcel, exportReportPdf } from './export';

export const GeneralReport: React.FC = () => {
  const { t } = useLanguage();
  const {
    currencySymbol,
    mode,
    inventoryEntryMode,
    dateRange,
    startDate,
    endDate,
    handleModeChange,
    handleRangeChange,
    handleStartDateChange,
    handleEndDateChange,
    loadingByMode,
    errorByMode,
    roastingReport,
    greenBeansReport,
    roastedBeansReport,
    finishedGoodsReport,
    businessReport,
    businessEntryMode,
    customerListReport,
    selectedCustomerId,
    selectCustomer,
    customerDetail,
    customerDetailLoading,
    customerDetailError,
    openTracking,
    selectedTrackingItem,
    closeTracking,
    roastingRows,
    roastingSortConfig,
    sortRoastingRows,
    greenBeansRows,
    greenBeansSortConfig,
    sortGreenBeansRows,
    roastedBeansRows,
    roastedBeansSortConfig,
    sortRoastedBeansRows,
    finishedGoodsRows,
    finishedGoodsSortConfig,
    sortFinishedGoodsRows,
  } = useReportPageState();
  const { showToast } = useToast();
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const handleExportPdf = async () => {
    try {
      setIsExportingPdf(true);
      const { partialFailures, chartSkipped } = await exportReportPdf({
        mode,
        startDate,
        endDate,
        currencySymbol,
        t,
        roastingReport,
        greenBeansReport,
        roastedBeansReport,
        finishedGoodsReport,
        businessReport,
        customerReport: customerListReport,
      });
      showToast(t('reports.export.pdfSuccess'), 'success');
      if (partialFailures > 0) showToast(t('reports.export.partialFailure', { count: partialFailures }), 'error');
      if (chartSkipped) showToast(t('reports.export.chartSkipped'), 'error');
    } catch (error) {
      console.error(error);
      showToast(t('reports.export.pdfFailed'), 'error');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsExportingExcel(true);
      const { partialFailures } = await exportReportExcel({
        mode,
        startDate,
        endDate,
        currencySymbol,
        t,
        roastingReport,
        greenBeansReport,
        roastedBeansReport,
        finishedGoodsReport,
        businessReport,
        customerReport: customerListReport,
      });
      showToast(t('reports.export.excelSuccess'), 'success');
      if (partialFailures > 0) showToast(t('reports.export.partialFailure', { count: partialFailures }), 'error');
    } catch (error) {
      console.error(error);
      showToast(t('reports.export.excelFailed'), 'error');
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <div className="space-y-6" data-test-id="general-report-container">
      <GeneralReportHeader
        mode={mode}
        onExportPdf={handleExportPdf}
        onExportExcel={handleExportExcel}
        isExportingPdf={isExportingPdf}
        isExportingExcel={isExportingExcel}
      />
      <DateRangeFilter
        dateRange={dateRange}
        startDate={startDate}
        endDate={endDate}
        onRangeChange={handleRangeChange}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        className="w-fit"
      />

      <ReportModeSelector mode={mode} onModeChange={handleModeChange} inventoryEntryMode={inventoryEntryMode} businessEntryMode={businessEntryMode} />

      {mode === 'roasting' && (
        <RoastingReportSection
          summary={roastingReport.summary}
          rows={roastingRows}
          loading={loadingByMode.roasting}
          error={errorByMode.roasting}
          sortConfig={roastingSortConfig}
          onSort={sortRoastingRows}
        />
      )}

      {mode === 'greenBeans' && (
        <GreenBeansReportSection
          summary={greenBeansReport.summary}
          rows={greenBeansRows}
          loading={loadingByMode.greenBeans}
          error={errorByMode.greenBeans}
          currencySymbol={currencySymbol}
          sortConfig={greenBeansSortConfig}
          onSort={sortGreenBeansRows}
          onRowClick={(itemId) => openTracking(itemId, 'green_bean')}
        />
      )}

      {mode === 'roastedBeans' && (
        <RoastedBeansReportSection
          summary={roastedBeansReport.summary}
          rows={roastedBeansRows}
          loading={loadingByMode.roastedBeans}
          error={errorByMode.roastedBeans}
          currencySymbol={currencySymbol}
          sortConfig={roastedBeansSortConfig}
          onSort={sortRoastedBeansRows}
          onRowClick={(itemId) => openTracking(itemId, 'roasted_bulk')}
        />
      )}

      {mode === 'finishedGoods' && (
        <FinishedGoodsReportSection
          summary={finishedGoodsReport.summary}
          rows={finishedGoodsRows}
          loading={loadingByMode.finishedGoods}
          error={errorByMode.finishedGoods}
          currencySymbol={currencySymbol}
          sortConfig={finishedGoodsSortConfig}
          onSort={sortFinishedGoodsRows}
          onRowClick={(itemId) => openTracking(itemId, 'finished_good')}
        />
      )}

      {mode === 'business' && (
        <>
          <BusinessReportSection
            report={businessReport}
            loading={loadingByMode.business}
            error={errorByMode.business}
            currencySymbol={currencySymbol}
          />
          <ReceivablesSection endDate={endDate} currencySymbol={currencySymbol} />
        </>
      )}

      {mode === 'customer' && (
        <CustomerReportSection
          list={customerListReport}
          loading={loadingByMode.customer}
          error={errorByMode.customer}
          currencySymbol={currencySymbol}
          selectedCustomerId={selectedCustomerId}
          onSelectCustomer={selectCustomer}
          detail={customerDetail}
          detailLoading={customerDetailLoading}
          detailError={customerDetailError}
        />
      )}

      <div className="text-xs text-gray-500 px-1" data-test-id="report-backend-integration-note">
        {t('reports.export.dataSourceNote')}
      </div>

      <InventoryTrackingDialog
        isOpen={!!selectedTrackingItem}
        itemId={selectedTrackingItem?.itemId ?? null}
        itemType={selectedTrackingItem?.itemType ?? null}
        onClose={closeTracking}
      />
    </div>
  );
};
